import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { memberAccessApi } from '../services/memberAccessApi';
import { storeAccessApi } from '../services/storeAccessApi';
import { evaluateMemberAccess } from '../services/accessService';

export default function useMemberAccess() {
  const { user } = useAuthenticator((context) => [context.user]);
  const userID = user?.attributes?.sub;
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState({ userID: null, grant: null, store: null, loading: Boolean(userID), available: true, storeError: '' });
  const refreshMembership = useCallback(() => setRevision(value => value + 1), []);
  useEffect(() => {
    if (!userID) {
      setState({ userID: null, grant: null, store: null, loading: false, available: true, storeError: '' });
      return undefined;
    }
    let active = true;
    let generation = 0;
    const refresh = async () => {
      const current = ++generation;
      const [grantResult, storeResult] = await Promise.allSettled([memberAccessApi.get(userID), storeAccessApi.get(userID)]);
      if (!active || current !== generation) return;
      setState({ userID, grant: grantResult.status === 'fulfilled' ? grantResult.value.grant : null,
        available: grantResult.status !== 'fulfilled' || grantResult.value.available,
        store: storeResult.status === 'fulfilled' ? storeResult.value : null,
        storeError: storeResult.status === 'rejected' ? 'Apple membership could not be verified. Please retry.' : '', loading: false });
    };
    setState(previous => ({ ...(previous.userID === userID ? previous : { grant: null, store: null, available: true, storeError: '' }), userID, loading: true }));
    void refresh();
    const onFocus = () => { if (!document.hidden) void refresh(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    const timer = setInterval(onFocus, 60000);
    return () => { active = false; generation++; clearInterval(timer); window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onFocus); };
  }, [userID, revision]);
  const current = state.userID === userID ? state : { grant: null, store: null, available: true, loading: Boolean(userID), storeError: '' };
  return useMemo(() => ({
    ...evaluateMemberAccess({ user, grant: current.grant, store: current.store, loading: current.loading }),
    complimentaryFeatureAvailable: current.available, store: current.store, storeError: current.storeError, refreshMembership,
  }), [user, current.grant, current.store, current.loading, current.available, current.storeError, refreshMembership]);
}
